import { useState, useRef } from "react";
import { dismissModal } from "@/utils/modalClose";
import { createPortal } from "react-dom";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon,
  IonCard, IonInput, IonSpinner,
} from "@ionic/react";
import { closeOutline, cameraOutline } from "ionicons/icons";
import { FLOAT } from "@/components/SettingsRows";
import { uiAvatar } from "@/components/SidebarProfileCard";
import { invalidateSavedAssets } from "@/components/SavedAssets";
import { emitAppLive } from "@/utils/appLive";
import { formatPhoneNumber } from "@/utils/validation";
import { userHeaders } from "@/utils/userAuth";
import { squareDataUrl } from "@/utils/imageResize";
import { toast } from "@/utils/toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// "Add your business" — offered once, right after a Pro or Enterprise plan
// activates. The same portal sheet as the other /app modals; the X skips
// (the sidebar's business card keeps offering it), Save stores the business
// and the sidebar card picks it up at once. Everything is editable later on
// /app/business.
//
// mode="create" adds a FURTHER business to the account instead of filling the
// first one in: it posts to the businesses collection, and every added
// business pays its own way — the caller sends them on to pick its plan.
export default function BusinessSetupModal({ onClose: onCloseNow, onSaved, mode = "setup" }) {
  const onClose = (e) => dismissModal(onCloseNow, e); // animated exit, then the real close

  const isMobile = window.innerWidth < 768;
  const [form, setForm] = useState({ name: "", email: "", phone: "", website: "", address: "", city: "", state: "", zip: "" });
  const [logo, setLogo] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.detail.value ?? "" }));

  const onLogoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Use a PNG, JPG, WebP, GIF or SVG."); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Image must be under 4 MB."); return; }
    try { setLogo(await squareDataUrl(file, 512)); }
    catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter your business name"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}${mode === "create" ? "/api/user/businesses" : "/api/user/business"}`, {
        method: mode === "create" ? "POST" : "PUT",
        headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ ...form, logo }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save");
      invalidateSavedAssets(); // the sidebar's business card reads the same response
      // a new business becomes the one being operated as, so the employee
      // and signature lists on screen re-scope, as they do on a switch
      if (mode === "create") emitAppLive({ reason: "business-switch" });
      toast.success(mode === "create" ? `${form.name.trim()} added` : "Your business is set up");
      onSaved?.(d.business);
      onClose();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const s = {
    backdrop: { position: "fixed", inset: 0, zIndex: 10014, background: isMobile ? "var(--ion-background-color)" : "rgba(0,0,0,0.55)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: "center" },
    sheet: { background: "var(--ion-background-color)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : 600, height: isMobile ? "100%" : "auto", maxHeight: isMobile ? "100%" : "85vh", overflow: "hidden" },
  };
  return createPortal(
    <div className="modal-backdrop pm-modal business-setup-modal" style={s.backdrop}>
      <div className="modal-slide-up" style={s.sheet}>
        <IonHeader>
          <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
            <IonButtons slot="start">
              <IonButton fill="clear" shape="round" onClick={onClose} aria-label="Close">
                <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                  <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                </span>
              </IonButton>
            </IonButtons>
            <IonTitle style={{ fontWeight: 700 }}>{mode === "create" ? "Add a business" : "Add your business"}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <div className="pm-body">
          <IonCard className="pm-card sheet-card">
            <div className="sheet-card-title">Your business</div>
            <div className="admin-subbar sheet-card-head">Business details</div>
            <div className="pm-form">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <img alt="" src={logo || uiAvatar(form.name || "Business")} style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                <IonButton size="small" color="light" onClick={() => fileRef.current?.click()} style={{ margin: 0 }}>
                  <IonIcon icon={cameraOutline} slot="start" />Upload logo
                </IonButton>
                {logo && <IonButton size="small" fill="clear" color="danger" onClick={() => setLogo("")} style={{ margin: 0 }}>Remove</IonButton>}
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onLogoFile} />
              </div>
              <IonInput {...FLOAT} label="Business name *" value={form.name} onIonInput={set("name")} />
              <IonInput {...FLOAT} label="Email" type="email" inputmode="email" value={form.email} onIonInput={set("email")} />
              <IonInput {...FLOAT} label="Phone" type="tel" inputmode="tel" value={form.phone}
                onIonInput={(e) => { const val = formatPhoneNumber(e.detail.value ?? ""); if (e.target) e.target.value = val; setForm((f) => ({ ...f, phone: val })); }} />
              <IonInput {...FLOAT} label="Website" type="url" inputmode="url" value={form.website} onIonInput={set("website")} />
            </div>
            <div className="admin-subbar sheet-card-head">Address</div>
            <div className="pm-form">
              <IonInput {...FLOAT} label="Street address" value={form.address} onIonInput={set("address")} />
              <IonInput {...FLOAT} label="City" value={form.city} onIonInput={set("city")} />
              <div style={{ display: "flex", gap: 10 }}>
                <IonInput {...FLOAT} label="State" value={form.state} onIonInput={set("state")} style={{ flex: 1 }} />
                <IonInput {...FLOAT} label="ZIP" value={form.zip} onIonInput={set("zip")} style={{ flex: 1 }} />
              </div>
              {mode === "create" && (
                <p className="admin-profile-sheet-note" style={{ margin: 0 }}>
                  Each business runs on its own subscription. After saving, you pick the plan it runs on.
                </p>
              )}
              <IonButton expand="block" color="success" disabled={busy} onClick={save}>
                {busy ? <IonSpinner name="crescent" /> : mode === "create" ? "Save & choose a plan" : "Save business"}
              </IonButton>
            </div>
          </IonCard>
        </div>
      </div>
    </div>,
    document.querySelector("ion-app") || document.body
  );
}
